import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  LoadFlowMergedScenariosResult,
  LoadFlowScenarioElementCalculation,
  LoadFlowScenarioInputs,
  PowerTransformerEndObject
} from '../../../..';
import { Voltage } from '../../../../domain/Voltage';
import type { ForStoringAttentionStatus } from '../../../../evaluation/AttentionStatusRepository';
import type { ForPowerTransformerEvaluation } from '../../../../evaluation/loadFlow/gridElements/powerTransformer/forPowerTransformerEvaluation';
import { PowerTransformerEvaluation } from '../../../../evaluation/loadFlow/gridElements/powerTransformer/powerTransformerEvaluation';
import type { FunctionalGrid } from '../../../../grid/FunctionalGrid';
import {
  findGridElementLabel,
  findGridElementTitle
} from '../../findGridElementLabel';
import type { PowerTransformerLoadFlowDetails } from '../gridElementLoadFlowDetails.types';
import { getSumActivePowerAndSumReactivePower } from './sumActiveAndReactivePowerLoadFlowDetails';
import { Features } from '../../../../createGridElementCalculationSetExecutionDetails';

export function createPowerTransformersLoadFlowDetails(
  features: Features,
  evaluationEngine: ForPowerTransformerEvaluation,
  results: LoadFlowMergedScenariosResult[],
  functionalGrid: FunctionalGrid,
  inputs: LoadFlowScenarioInputs,
  attentionStatusRepository: ForStoringAttentionStatus
): PowerTransformerLoadFlowDetails[] {
  const evaluatedResults = PowerTransformerEvaluation.fromResults(
    results,
    attentionStatusRepository
  ).evaluateEachPowerTransformer(evaluationEngine);

  const result: PowerTransformerLoadFlowDetails[] = evaluatedResults.map(
    evaluatedPowerTransformer => {
      const { sumActivePower, sumReactivePower } =
        getSumActivePowerAndSumReactivePower(
          evaluatedPowerTransformer,
          functionalGrid,
          inputs
        );

      const { tertiaryEnd, primaryEnd, secondaryEnd } = getEnds(
        evaluatedPowerTransformer,
        functionalGrid,
        results
      );
      let powerTransformerType: 'ThreeWinding' | 'TwoWinding';
      if (tertiaryEnd === undefined) {
        powerTransformerType = 'TwoWinding';
        return {
          ...evaluatedPowerTransformer,
          label: findGridElementLabel(features, evaluatedPowerTransformer.id),
          title: findGridElementTitle(features, evaluatedPowerTransformer.id),
          primaryVoltage: new Voltage(primaryEnd?.result.voltage, 'V'),
          secondaryVoltage: new Voltage(secondaryEnd?.result.voltage),
          powerTransfomerType: powerTransformerType,
          sumActivePower: sumActivePower.toKilo(),
          sumReactivePower: sumReactivePower.toKilo()
        };
      } else {
        powerTransformerType = 'ThreeWinding';
        return {
          ...evaluatedPowerTransformer,
          label: findGridElementLabel(features, evaluatedPowerTransformer.id),
          title: findGridElementTitle(features, evaluatedPowerTransformer.id),
          primaryVoltage: new Voltage(primaryEnd?.result.voltage, 'V'),
          secondaryVoltage: new Voltage(secondaryEnd?.result.voltage),
          tertiaryVoltage: new Voltage(tertiaryEnd?.result.voltage),
          powerTransfomerType: powerTransformerType,
          sumActivePower: sumActivePower.toKilo(),
          sumReactivePower: sumReactivePower.toKilo()
        };
      }
    }
  );
  return result;
}

function getEnds(
  evaluatedPowerTransformer: {
    id: `{${string}}`;
    objectType: 'PowerTransformer';
  },
  functionalGrid: FunctionalGrid,
  results: LoadFlowScenarioElementCalculation[]
) {
  const powerTransformerId = evaluatedPowerTransformer.id;
  const powerTransformerEnds = functionalGrid.powerTransformers.find(
    p => p.objectId === powerTransformerId
  )?.powerTransformerEnds;

  const powerTransformerEndsResults: PowerTransformerEndObject[] =
    results.filter(
      r => r.objectType === 'PowerTransformerEnd'
    ) as PowerTransformerEndObject[];

  const powerTransformerEndsResultsBelongingToTransformer =
    powerTransformerEndsResults.filter(r =>
      powerTransformerEnds?.includes(r.id)
    );
  const primary = functionalGrid.powerTransformerEnds.find(
    p => p.endNumber === 1 && powerTransformerEnds?.includes(p.objectId)
  );
  const secondary = functionalGrid.powerTransformerEnds.find(
    p => p.endNumber === 2 && powerTransformerEnds?.includes(p.objectId)
  );
  const tertiary = functionalGrid.powerTransformerEnds.find(
    p => p.endNumber === 3 && powerTransformerEnds?.includes(p.objectId)
  );

  const primaryEnd = powerTransformerEndsResultsBelongingToTransformer.find(
    r => r.id === primary?.objectId
  );
  const secondaryEnd = powerTransformerEndsResultsBelongingToTransformer.find(
    r => r.id === secondary?.objectId
  );
  const tertiaryEnd = powerTransformerEndsResultsBelongingToTransformer.find(
    r => r.id === tertiary?.objectId
  );

  if (primaryEnd === undefined || secondaryEnd === undefined) {
    throw new Error(
      `Primary  results not found for transformer ${powerTransformerId}`
    );
  }
  return { tertiaryEnd, primaryEnd, secondaryEnd };
}
